import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

export interface GameButtonsCallbacks {
  onCreateRoom: () => void;
  onJoinRoom: () => void;
  onStartGame: () => void;
  onLeaveRoom: () => void;
}

/**
 * Manages the Create, Join, Start, and Leave buttons in the lobby.
 */
export class GameButtons {
  private createButton: Phaser.GameObjects.Container;
  private joinButton: Phaser.GameObjects.Container;
  private startButton: Phaser.GameObjects.Container;
  private leaveButton: Phaser.GameObjects.Container;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: GameButtonsCallbacks
  ) {
    this.createButton = this.buildCreateButton();
    this.joinButton = this.buildJoinButton();
    this.startButton = this.buildStartButton();
    this.leaveButton = this.buildLeaveButton();
  }

  private buildCreateButton(): Phaser.GameObjects.Container {
    return this.createUIButton(GAME_WIDTH / 2 - 110, 520, 'Create Room', () => {
      this.callbacks.onCreateRoom();
    });
  }

  private buildJoinButton(): Phaser.GameObjects.Container {
    return this.createUIButton(GAME_WIDTH / 2 + 110, 520, 'Join Room', () => {
      this.callbacks.onJoinRoom();
    });
  }

  private createUIButton(x: number, y: number, text: string, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const bg = this.scene.add
      .rectangle(0, 0, 180, 50, PERSIAN_THEME.colors.buttonBg)
      .setStrokeStyle(2, PERSIAN_THEME.colors.bronze);

    const label = this.scene.add
      .text(0, 0, text, {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: PERSIAN_THEME.hex.textPrimary,
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(180, 50);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonHover));
    container.on('pointerout', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonBg));
    container.on('pointerdown', onClick);

    return container;
  }

  private buildStartButton(): Phaser.GameObjects.Container {
    const container = this.scene.add.container(GAME_WIDTH / 2, 510);

    const bg = this.scene.add
      .rectangle(0, 0, 130, 38, PERSIAN_THEME.colors.gold)
      .setStrokeStyle(2, PERSIAN_THEME.colors.bronze);

    const label = this.scene.add
      .text(0, 0, 'Start Game', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '16px',
        color: PERSIAN_THEME.hex.feltGreen,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(130, 38);
    container.setInteractive({ useHandCursor: true });
    container.setVisible(false);

    // Subtle pulsing glow animation
    this.scene.tweens.add({
      targets: container,
      scaleX: 1.03,
      scaleY: 1.03,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    container.on('pointerover', () => bg.setFillStyle(0xe4bf47));
    container.on('pointerout', () => bg.setFillStyle(PERSIAN_THEME.colors.gold));
    container.on('pointerdown', () => {
      // Disable to prevent double-clicks during game start sequence
      container.disableInteractive();
      this.callbacks.onStartGame();
    });

    return container;
  }

  private buildLeaveButton(): Phaser.GameObjects.Container {
    const container = this.scene.add.container(50, 30);

    const bg = this.scene.add
      .rectangle(0, 0, 70, 28, PERSIAN_THEME.colors.tableCenter, 0.9)
      .setStrokeStyle(1, PERSIAN_THEME.colors.bronze);

    const label = this.scene.add
      .text(0, 0, 'Leave', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(70, 28);
    container.setInteractive({ useHandCursor: true });
    container.setVisible(false);

    container.on('pointerover', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonHover));
    container.on('pointerout', () => bg.setFillStyle(PERSIAN_THEME.colors.buttonBg));
    container.on('pointerdown', () => this.callbacks.onLeaveRoom());

    return container;
  }

  showJoinUI(): void {
    this.createButton.setVisible(true);
    this.joinButton.setVisible(true);
    this.createButton.setInteractive({ useHandCursor: true });
    this.joinButton.setInteractive({ useHandCursor: true });
  }

  hideJoinUI(): void {
    this.createButton.setVisible(false);
    this.joinButton.setVisible(false);
  }

  disableJoinButtons(): void {
    this.createButton.disableInteractive();
    this.joinButton.disableInteractive();
  }

  showLeaveButton(): void {
    this.leaveButton.setVisible(true);
  }

  hideLeaveButton(): void {
    this.leaveButton.setVisible(false);
  }

  showStartButton(): void {
    if (this.startButton.visible) return;

    // Intro animation: fade in and slide up
    this.startButton.setVisible(true);
    this.startButton.setAlpha(0);
    this.startButton.y += 20;
    this.scene.tweens.add({
      targets: this.startButton,
      alpha: 1,
      y: 510,
      duration: 400,
      ease: 'Back.easeOut',
    });
  }

  hideStartButton(): void {
    this.startButton.setVisible(false);
  }

  enableStartButton(): void {
    this.startButton.setInteractive({ useHandCursor: true });
  }

  isStartButtonVisible(): boolean {
    return this.startButton.visible;
  }

  /**
   * Reset room UI state (hide start button, re-enable buttons).
   * Call when leaving a room.
   */
  reset(): void {
    this.startButton.setVisible(false);
    this.startButton.setInteractive({ useHandCursor: true });
    // Re-enable join buttons (they get disabled during create/join)
    this.createButton.setInteractive({ useHandCursor: true });
    this.joinButton.setInteractive({ useHandCursor: true });
  }

  destroy(): void {
    this.createButton?.destroy();
    this.joinButton?.destroy();
    this.startButton?.destroy();
    this.leaveButton?.destroy();
  }

  getJoinUIContainers(): Phaser.GameObjects.Container[] {
    return [this.createButton, this.joinButton];
  }

  getRoomUIContainers(): Phaser.GameObjects.Container[] {
    // Only include leaveButton - startButton visibility is managed by checkStartButton()
    return [this.leaveButton];
  }
}
